import React, { useCallback, useEffect, useState } from "react";
import { useTabs } from "../../hooks/lib/useTabs";
import { useMobile } from "../../hooks/custom/useMobile";
import { Link, useLocation } from "react-router-dom";
import { dynamicFailed } from "../../hooks/custom/useToast";
import { useLoader } from "../../context/LoaderContext";
import {
  fetchAcademicsAdmissionOpen,
  fetchAcademicsApplicationEligibility,
  fetchAcademicsCurriculum,
  fetchAcademicsFeeScholarship,
  fetchAcademicsFirstCohort,
  fetchAcademicsKeyFeatures,
  fetchAcademicsMPPTabImage,
  fetchAcademicsWhyMpp,
} from "../../api/GraduateProgramsApi";
import { IAcademicsMppWhyMppData } from "../../exports/interfaces";
import { PdfViwer } from "../PdfViwer";
import { useBrochureContext } from "../../context/BrochureContext";
import Calendar from "./Calendar";
import TabSidebar from "../TabSidebar";
import {
  getVisibleTabs,
  stripHtml,
  truncateText,
} from "../../exports/functions";

const GraduateTab = () => {
  useTabs();
  const { setLoading } = useLoader();
  const isMobile = useMobile();

  const [openSidebar, setOpenSidebar] = useState(false);
  const location = useLocation();
  const { mppBrochureData } = useBrochureContext();

  const [openPdf, setOpenPdf] = useState(false);
  const [pdfPath, setPdfPath] = useState("");

  const [tabImage, setTabImage] = useState<
    { image: [{ media: { url: string } }] }[]
  >([]);

  const openViewer = (path: string) => {
    setPdfPath(path);
    setOpenPdf(true);
  };

  const closeViewer = () => setOpenPdf(false);

  const [activeTab, setActiveTab] = useState<string>("#td_tab_1");

  const [allTabData, setAllTabData] = useState<{
    [key: string]: IAcademicsMppWhyMppData[];
  }>({});

  const allTabs = React.useMemo(
    () => [
      { id: "#td_tab_1", label: "Why MPP?" },
      { id: "#td_tab_2", label: "Key Features" },
      { id: "#td_tab_3", label: "Admission" },
      { id: "#td_tab_4", label: "Curriculum" },
      { id: "#td_tab_5", label: "Fee & Scholarship" },
      { id: "#td_tab_6", label: "Academic Calendar" },
      { id: "#td_tab_7", label: "First Cohort" },
    ],
    [],
  );

  const visibleTabs = React.useMemo(() => {
    return getVisibleTabs(allTabs, activeTab, isMobile ? 2 : 4);
  }, [allTabs, activeTab, isMobile]);

  useEffect(() => {
    if (!location.hash) return;

    const hash = location.hash;

    const timer = setTimeout(() => {
      const tabLink = document.querySelector(
        `.td_tab_links a[href="${hash}"]`,
      ) as HTMLAnchorElement | null;

      tabLink?.click();

      const tabsWrapper = document.querySelector(".td_tabs");
      tabsWrapper?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [location.hash]);

  const openTab = (hash: string) => {
    const tabLink = document.querySelector(
      `.td_tab_links a[href="${hash}"]`,
    ) as HTMLAnchorElement | null;

    tabLink?.click();

    const tabsWrapper = document.querySelector(".td_tabs");
    tabsWrapper?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchAcademicsMPPTabImage()])
      .then(([tabImageData]) => {
        setTabImage(tabImageData);
      })
      .catch((error) => {
        dynamicFailed(error.response.data.error.message);
      })
      .finally(() => {
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      });
  }, [setLoading]);

  const fetchAllTabData = useCallback(async () => {
    try {
      setLoading(true);

      const [
        whyMpp,
        keyFeatures,
        admissionOpen,
        curriculum,
        feeScholarship,
        applicationEligibility,
        firstCohort,
      ] = await Promise.all([
        fetchAcademicsWhyMpp(),
        fetchAcademicsKeyFeatures(),
        fetchAcademicsAdmissionOpen(),
        fetchAcademicsCurriculum(),
        fetchAcademicsFeeScholarship(),
        fetchAcademicsApplicationEligibility(),
        fetchAcademicsFirstCohort(),
      ]);

      setAllTabData({
        "#td_tab_1": whyMpp,
        "#td_tab_2": keyFeatures,
        "#td_tab_3": admissionOpen,
        "#td_tab_4": curriculum,
        "#td_tab_5": feeScholarship,
        "#td_tab_6": applicationEligibility,
        "#td_tab_7": firstCohort,
      });
    } catch (error: any) {
      dynamicFailed(error.response?.data?.error?.message);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, [setLoading]);

  useEffect(() => {
    fetchAllTabData();
  }, [fetchAllTabData]);

  const handleTabClick = (hash: string) => {
    setActiveTab(hash);
    openTab(hash);
  };

  const renderTabContent = (description: string) => {
    if (!description.toLowerCase().includes("online application")) {
      return <span dangerouslySetInnerHTML={{ __html: description }} />;
    }

    const updatedHtml = description.replace(
      /Visit the University of Nepal website and complete the online application form:<br\s*\/?>/i,
      `Visit the University of Nepal website and complete the online application form:<br />
    <a
      href="/application-form"
      class="td_btn td_style_1 td_radius_10 td_medium"
      style="cursor: not-allowed;" onClick="event.preventDefault();"
    >
      <span class="td_btn_in td_white_color td_orange_bg disabled_btn">
        <span>Apply Now</span>
        <svg
          width="19"
          height="20"
          viewBox="0 0 19 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15.1575 4.34302L3.84375 15.6567"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M15.157 11.4142C15.157 11.4142 16.0887 5.2748 15.157 4.34311C14.2253 3.41142 8.08594 4.34314 8.08594 4.34314"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
    </a>`,
    );

    return <span dangerouslySetInnerHTML={{ __html: updatedHtml }} />;
  };

  const tabData = allTabData[activeTab] || [];

  return (
    <>
      <section>
        <div className="td_height_120 td_height_lg_40" />
        <div className="container">
          <div className="row td_gap_y_50">
            {/* content */}
            <div className="td_course_details">
              {/* tabs */}
              <div className="td_tabs td_style_1">
                <ul className="td_tab_links td_style_2 td_type_2 td_mp_0 td_medium td_fs_16 td_heading_color graduate_tabs align-items-center justify-content-between">
                  <div className="td_tab_links td_style_2">
                    {visibleTabs.map((tab) => (
                      <li
                        key={tab.id}
                        className={activeTab === tab.id ? "active" : ""}
                      >
                        <a
                          href={tab.id}
                          onClick={() => handleTabClick(tab.id)}
                          data-bs-toggle="tooltip"
                          data-bs-placement="top"
                          title={tab.label}
                        >
                          {truncateText(tab.label, isMobile ? 10 : 30)}
                        </a>
                      </li>
                    ))}
                  </div>
                  <li>
                    <div className="course_sidebar_toggle_wrapper">
                      <button
                        className="course_sidebar_toggle mt-1"
                        onClick={() => setOpenSidebar(true)}
                      >
                        <span></span>
                        <span></span>
                        <span></span>
                        {/* <span></span>
                  <span></span>
                  <span></span> */}
                      </button>
                    </div>
                  </li>
                </ul>
                <div className="td_tab_body td_fs_18">
                  <div
                    className={`td_tab active custom_td_tab ${activeTab === "#td_tab_1" && "active_custom_td_tab"}`}
                    id="td_tab_1"
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "25px",
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      {isMobile && (
                        <div
                          className="tab-image-wrapper"
                          style={{ width: "100%" }}
                        >
                          <img
                            src={
                              process.env.REACT_APP_cmsUrl +
                              tabImage[0]?.image[0]?.media?.url
                            }
                            alt="student2"
                            height="auto"
                            width="100%"
                          />
                        </div>
                      )}{" "}
                      <div className="mb-0 custom-text-p" style={{ flex: 1 }}>
                        {tabData.map((data, index) => (
                          <>
                            {data?.title && <h5>{data?.title}</h5>}
                            <span
                              dangerouslySetInnerHTML={{
                                __html: data?.description,
                              }}
                            />
                            {index !== tabData.length - 1 && (
                              <div className="td_height_30 td_height_lg_30" />
                            )}
                          </>
                        ))}
                        {activeTab === "#td_tab_1" && (
                          <button
                            type="button"
                            onClick={() => handleTabClick("#td_tab_4")}
                            className="td_btn td_style_1 td_radius_10 td_medium custom-td_style_1 bg-transparent"
                          >
                            <span className="td_btn_in custom_td_btn_in act_btn">
                              <span>Explore Curriculum</span>
                            </span>
                          </button>
                        )}
                      </div>
                      {!isMobile && (
                        <div className="tab-image-wrapper">
                          <img
                            src={
                              process.env.REACT_APP_cmsUrl +
                              tabImage[0]?.image[0]?.media?.url
                            }
                            alt="student2"
                            height="100px"
                            width="auto"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    className={`td_tab active custom_td_tab ${activeTab === "#td_tab_2" && "active_custom_td_tab"}`}
                    id="td_tab_2"
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "25px",
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      {isMobile && (
                        <div
                          className="tab-image-wrapper"
                          style={{ width: "100%" }}
                        >
                          <img
                            src={
                              process.env.REACT_APP_cmsUrl +
                              tabImage[0]?.image[0]?.media?.url
                            }
                            alt="student2"
                            height="auto"
                            width="100%"
                          />
                        </div>
                      )}{" "}
                      <div
                        className="mb-0 w-100 custom-text-p"
                        style={{ flex: 1 }}
                      >
                        {tabData.map((data, index) => (
                          <>
                            {data?.title && <h5>{data?.title}</h5>}
                            <span
                              dangerouslySetInnerHTML={{
                                __html: data?.description,
                              }}
                            />
                            {index !== tabData.length - 1 && (
                              <div className="td_height_30 td_height_lg_30" />
                            )}
                          </>
                        ))}
                      </div>
                      {!isMobile && (
                        <div className="tab-image-wrapper">
                          <img
                            src={
                              process.env.REACT_APP_cmsUrl +
                              tabImage[0]?.image[0]?.media?.url
                            }
                            alt="student2"
                            height="100px"
                            width="auto"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    className={`td_tab active custom_td_tab ${activeTab === "#td_tab_3" && "active_custom_td_tab"}`}
                    id="td_tab_3"
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "25px",
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      {isMobile && (
                        <div
                          className="tab-image-wrapper"
                          style={{ width: "100%" }}
                        >
                          <img
                            src={
                              process.env.REACT_APP_cmsUrl +
                              tabImage[0]?.image[0]?.media?.url
                            }
                            alt="student2"
                            height="auto"
                            width="100%"
                          />
                        </div>
                      )}{" "}
                      <div
                        className="mb-0 w-100 custom-text-p"
                        style={{ flex: 1 }}
                      >
                        {tabData.map((data, index) => (
                          <>
                            {data?.title && <h5>{data?.title}</h5>}
                            {renderTabContent(data.description)}
                            {index !== tabData.length - 1 && (
                              <div className="td_height_30 td_height_lg_30" />
                            )}
                          </>
                        ))}
                      </div>
                      {!isMobile && (
                        <div className="tab-image-wrapper">
                          <img
                            src={
                              process.env.REACT_APP_cmsUrl +
                              tabImage[0]?.image[0]?.media?.url
                            }
                            alt="student2"
                            height="100px"
                            width="auto"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* <div className="td_tab" id="td_tab_2">
                  <div
                    style={{
                      display: "flex",
                      gap: "25px",
                      flexDirection: isMobile ? "column" : "row",
                    }}
                  >
                    {isMobile && (
                      <div
                        className="tab-image-wrapper"
                        style={{ width: "100%" }}
                      >
                        <img
                          src={student}
                          alt="student2"
                          height="auto"
                          width="100%"
                        />
                      </div>
                    )}{" "}
                    <div
                      className="mb-0 w-100"
                      style={{ textAlign: "justify", flex: 1 }}
                    >
                      <p>
                        <p className="mt-1">
                          Tuition for the Master of Public Policy (MPP) is Rs.
                          400,000 per year, totalling Rs. 800,000 for the full
                          two-year program.
                        </p>
                      </p>

                      <p>
                        <p className="mt-1">
                          Scholarships are available through the University
                          Grants Commission to help offset the cost.
                        </p>
                      </p>

                      <p>
                        <p className="mt-1">
                          Eligible students may apply for financial support;
                          details on how to apply for scholarships will be
                          provided with the application materials.
                        </p>
                      </p>
                    </div>
                    {!isMobile && (
                      <div className="tab-image-wrapper">
                        <img
                          src={student}
                          alt="student2"
                          height="100px"
                          width="auto"
                        />
                      </div>
                    )}
                  </div>
                </div> */}

                  <div
                    className={`td_tab active custom_td_tab ${activeTab === "#td_tab_4" && "active_custom_td_tab"}`}
                    id="td_tab_4"
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "25px",
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      {isMobile && (
                        <div
                          className="tab-image-wrapper"
                          style={{ width: "100%" }}
                        >
                          <img
                            src={
                              process.env.REACT_APP_cmsUrl +
                              tabImage[0]?.image[0]?.media?.url
                            }
                            alt="student2"
                            height="auto"
                            width="100%"
                          />
                        </div>
                      )}{" "}
                      <div
                        className="mb-0 w-100 custom-text-p"
                        style={{ flex: 1 }}
                      >
                        {tabData.map((data, index) => (
                          <>
                            {data?.title && <h5>{data?.title}</h5>}
                            <span
                              dangerouslySetInnerHTML={{
                                __html: data?.description,
                              }}
                            />
                            {index !== tabData.length - 1 && (
                              <div className="td_height_30 td_height_lg_30" />
                            )}
                          </>
                        ))}
                      </div>
                      {!isMobile && (
                        <div className="tab-image-wrapper">
                          <img
                            src={
                              process.env.REACT_APP_cmsUrl +
                              tabImage[0]?.image[0]?.media?.url
                            }
                            alt="student2"
                            height="100px"
                            width="auto"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    className={`td_tab active custom_td_tab ${activeTab === "#td_tab_5" && "active_custom_td_tab"}`}
                    id="td_tab_5"
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "25px",
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      {isMobile && (
                        <div
                          className="tab-image-wrapper"
                          style={{ width: "100%" }}
                        >
                          <img
                            src={
                              process.env.REACT_APP_cmsUrl +
                              tabImage[0]?.image[0]?.media?.url
                            }
                            alt="student2"
                            height="auto"
                            width="100%"
                          />
                        </div>
                      )}{" "}
                      <div
                        className="mb-0 w-100 custom-text-p"
                        style={{ flex: 1 }}
                      >
                        {tabData.map((data, index) => (
                          <>
                            {data?.title && <h5>{data?.title}</h5>}
                            <span
                              dangerouslySetInnerHTML={{
                                __html: data?.description,
                              }}
                            />
                            {index !== tabData.length - 1 && (
                              <div className="td_height_30 td_height_lg_30" />
                            )}
                          </>
                        ))}
                        <Link
                          to="/application-form"
                          className="td_btn td_style_1 td_radius_10 td_medium"
                          onClick={(e) => e.preventDefault()}
                          style={{ cursor: "not-allowed" }}
                        >
                          <span className="td_btn_in td_white_color td_orange_bg disabled_btn">
                            <span>Apply Now</span>
                            <svg
                              width="19"
                              height="20"
                              viewBox="0 0 19 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M15.1575 4.34302L3.84375 15.6567"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M15.157 11.4142C15.157 11.4142 16.0887 5.2748 15.157 4.34311C14.2253 3.41142 8.08594 4.34314 8.08594 4.34314"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        </Link>
                      </div>
                      {!isMobile && (
                        <div className="tab-image-wrapper">
                          <img
                            src={
                              process.env.REACT_APP_cmsUrl +
                              tabImage[0]?.image[0]?.media?.url
                            }
                            alt="student2"
                            height="100px"
                            width="auto"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    className={`td_tab active custom_td_tab ${activeTab === "#td_tab_6" && "active_custom_td_tab"}`}
                    id="td_tab_6"
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "25px",
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      <div
                        className="mb-0 w-100 custom-text-p"
                        style={{ flex: 1 }}
                      >
                        <Calendar />
                      </div>
                    </div>
                  </div>

                  <div
                    className={`td_tab active custom_td_tab ${activeTab === "#td_tab_7" && "active_custom_td_tab"}`}
                    id="td_tab_7"
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "25px",
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      {isMobile && (
                        <div
                          className="tab-image-wrapper"
                          style={{ width: "100%" }}
                        >
                          <img
                            src={
                              process.env.REACT_APP_cmsUrl +
                              tabImage[0]?.image[0]?.media?.url
                            }
                            alt="student2"
                            height="auto"
                            width="100%"
                          />
                        </div>
                      )}{" "}
                      <div className="mb-0 custom-text-p" style={{ flex: 1 }}>
                        {tabData.map((data, index) => (
                          <>
                            {data?.title && <h5>{data?.title}</h5>}
                            <span
                              dangerouslySetInnerHTML={{
                                __html: data?.description,
                              }}
                            />
                            {index !== tabData.length - 1 && (
                              <div className="td_height_30 td_height_lg_30" />
                            )}
                          </>
                        ))}
                      </div>
                      {!isMobile && (
                        <div className="tab-image-wrapper">
                          <img
                            src={
                              process.env.REACT_APP_cmsUrl +
                              tabImage[0]?.image[0]?.media?.url
                            }
                            alt="student2"
                            height="100px"
                            width="auto"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="td_height_120 td_height_lg_80"></div>{" "}
      </section>
      <section>
        <div className="container d-flex justify-content-center">
          <Link
            to="#"
            onClick={(e) => {
              e.preventDefault();
              openViewer(
                mppBrochureData[0]?.file && mppBrochureData[0]?.file.length > 0
                  ? mppBrochureData[0]?.file[0]?.media?.url
                  : stripHtml(mppBrochureData[0]?.link),
              );
            }}
            className="td_btn td_style_1 td_radius_10 custom-td_style_1 td_medium"
          >
            <span className="td_btn_in custom_td_btn_in">
              <span>View Brochure </span>
              <svg
                width="19"
                height="20"
                viewBox="0 0 19 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.1575 4.34302L3.84375 15.6567"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15.157 11.4142C15.157 11.4142 16.0887 5.2748 15.157 4.34311C14.2253 3.41142 8.08594 4.34314 8.08594 4.34314"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </Link>
        </div>
        <div className="td_height_120 td_height_lg_40" />
      </section>
      <PdfViwer open={openPdf} onClose={closeViewer} path={pdfPath} />
      <TabSidebar
        title="MPP Tabs"
        tabs={allTabs}
        activeTab={activeTab}
        handleTabClick={handleTabClick}
        openSidebar={openSidebar}
        setOpenSidebar={setOpenSidebar}
      />
    </>
  );
};

export default GraduateTab;
